import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useState } from "react";
import { api, getToken, setToken, setUnauthorizedHandler } from "./api";
import type { Me, Role } from "./types";

interface Session {
  me: Me | undefined;
  loading: boolean;
  signedIn: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  switchTenant: (tenantId: string) => Promise<void>;
  hasRole: (role: Role) => boolean;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [token, setTokenState] = useState(getToken);

  const applyToken = useCallback(
    (t: string | null) => {
      setToken(t);
      setTokenState(t);
      qc.clear();
    },
    [qc],
  );

  useEffect(() => setUnauthorizedHandler(() => applyToken(null)), [applyToken]);

  const meQuery = useQuery({
    queryKey: ["me", token],
    queryFn: () => api<Me>("/auth/me"),
    enabled: !!token,
    staleTime: 60_000,
  });

  const value = useMemo<Session>(() => {
    const me = meQuery.data;
    return {
      me,
      loading: !!token && meQuery.isPending,
      signedIn: !!token && !!me,
      async signIn(email, password) {
        const { token } = await api<{ token: string }>("/auth/login", {
          method: "POST",
          body: { email, password },
        });
        applyToken(token);
      },
      signOut: () => applyToken(null),
      async switchTenant(tenantId) {
        const { token } = await api<{ token: string }>("/auth/switch", {
          method: "POST",
          body: { tenant_id: tenantId },
        });
        applyToken(token);
      },
      hasRole: (role) => !!me && (me.user.is_platform_admin || !!me.tenant?.roles.includes(role)),
    };
  }, [token, meQuery.data, meQuery.isPending, applyToken]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): Session {
  const s = use(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}
